/**
 * ユーザー情報統合 API サービス
 * プロフィールとサブスクリプション情報を統合して取得
 */

import type { components } from '@/types/api-schema';
import { DEFAULT_PLAN_LIMITS, getPlanDisplayName, formatLimitValue } from './subscription';
import { PLAN_TYPE } from '@/constants/planTypes';
import { throwIfRateLimited } from '@/services/apiErrors';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Use auto-generated types from OpenAPI schema (SSOT)
export type UserMe = components['schemas']['UserMe'];
export type UserProfile = components['schemas']['UserProfile'];
export type UsageSummary = components['schemas']['UsageSummary'];
export type PlanLimits = components['schemas']['PlanLimits'];
export type PlanType = components['schemas']['PlanType'];

export class UserNotFoundError extends Error {
  readonly status: number;

  constructor(message = 'User not found', status = 404) {
    super(message);
    this.name = 'UserNotFoundError';
    this.status = status;
  }
}

export class UserUnauthorizedError extends Error {
  readonly status: number;

  constructor(message = 'Unauthorized', status = 401) {
    super(message);
    this.name = 'UserUnauthorizedError';
    this.status = status;
  }
}

/**
 * ユーザー情報（プロフィール + サブスクリプション）を取得
 * @param token - Cognito JWT トークン
 */
export const getUserMe = async (token: string): Promise<UserMe> => {
  const response = await fetch(`${API_BASE_URL}/api/user/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    if (response.status === 401 || response.status === 403) {
      throw new UserUnauthorizedError('認証が必要です。ログインし直してください', response.status);
    }
    if (response.status === 404) {
      throw new UserNotFoundError('ユーザー情報が見つかりません', 404);
    }

    const error = await response
      .json()
      .catch(() => ({ message: 'ユーザー情報の取得に失敗しました' }));
    throw new Error(error.message || 'ユーザー情報の取得に失敗しました');
  }

  return response.json();
};

/**
 * プラン情報取得エラー時のデフォルトサブスクリプション値（無料プランとして扱う）
 * 使用量は0として扱い、バックエンドで最終的なチェックを行う
 */
export const DEFAULT_FREE_SUBSCRIPTION: UsageSummary = {
  planType: PLAN_TYPE.FREE,
  billingInterval: null,
  currentPeriodEnd: null,
  limits: DEFAULT_PLAN_LIMITS.free,
  usage: {
    eventsCreated: 0,
    aiGenerations: 0,
    urlImportsToday: 0,
    aiUrlImportsToday: 0,
  },
  remaining: {
    activeEvents: DEFAULT_PLAN_LIMITS.free.maxActiveEvents,
    aiGenerationsPerMonth: DEFAULT_PLAN_LIMITS.free.aiGenerationsPerMonth,
    urlImportsToday: DEFAULT_PLAN_LIMITS.free.urlImportsPerDay,
    aiUrlImportsToday: DEFAULT_PLAN_LIMITS.free.aiUrlImportsPerDay,
  },
};

// Re-export subscription helpers for convenience
export {
  DEFAULT_PLAN_LIMITS,
  formatLimitValue,
  getPlanDisplayName,
};
